import type { CSSProperties, ReactNode } from 'react'
import { cn } from '~/src/lib/utils'

/**
 * The report design-system comparison table, as a presentational frame: a
 * rounded card that scrolls sideways inside itself, a sticky spec-label column,
 * a lavender header band and a right-edge fade on mobile that says "there is
 * more to the right".
 *
 * Extracted from `HubComparisonTable` so the in-chat `ProductComparisonTable`
 * renders the same table instead of a second hand-rolled one. Callers own the
 * content (header nodes, cell nodes); the frame owns layout, stickiness and
 * scroll behaviour.
 *
 * `tone`:
 * - `report` (default) — the report pages' exact look, light only (they
 *   hardcode white and the Figma header tint).
 * - `adaptive` — the same look in light mode, plus dark-mode surfaces, for
 *   surfaces that follow the app theme (chat).
 */
export interface ComparisonTableFrameColumn {
  key: string
  header: ReactNode
  highlighted?: boolean
}

export interface ComparisonTableFrameRow {
  key: string
  label: ReactNode
  cells: ReactNode[]
  /** Per-cell extra classes, index-aligned with `cells`. */
  cellClassNames?: Array<string | undefined>
}

interface ComparisonTableFrameProps {
  columns: ComparisonTableFrameColumn[]
  rows: ComparisonTableFrameRow[]
  /** Screen-reader caption. */
  caption?: string
  /** Screen-reader name of the label column. */
  labelColumnName?: string
  /** Table min-width — px when a number, else any CSS length (e.g. a
   *  `calc()` over a custom property the caller sets responsively). The card
   *  scrolls below it. */
  minWidth: number | string
  /** Width of each product column, e.g. `25%`. Omit to share the remainder. */
  columnWidth?: string
  /** Width of the sticky label column. Omit to let the table size it. */
  labelColumnWidth?: string
  /** Classes for the label column's header cell — e.g. a responsive width,
   *  which inline `labelColumnWidth` cannot express. */
  labelColumnClassName?: string
  tone?: 'report' | 'adaptive'
  headerCellClassName?: string
  labelCellClassName?: string
  cellClassName?: string
  className?: string
  'data-testid'?: string
}

const TONE = {
  report: {
    card: 'bg-white',
    headerBorder: 'border-black/10',
    headerTint:
      'bg-[#F5F4F8]' /* color-token-exception: matches SeoReportComparisonTable header tint */,
    highlightTint:
      'bg-[rgba(244,243,255,0.3)]' /* color-token-exception: Figma highlight tint */,
    stickyLabel: 'bg-white',
    fade: 'to-white',
  },
  adaptive: {
    card: 'bg-white dark:bg-surface-raised',
    headerBorder: 'border-black/10 dark:border-white/10',
    headerTint:
      'bg-[#F5F4F8] dark:bg-surface-soft' /* color-token-exception: report header tint, light mode only */,
    highlightTint:
      'bg-[rgba(244,243,255,0.3)] dark:bg-white/5' /* color-token-exception: Figma highlight tint */,
    stickyLabel: 'bg-white dark:bg-surface-raised',
    fade: 'to-white dark:to-surface-raised',
  },
} as const

export function ComparisonTableFrame({
  columns,
  rows,
  caption,
  labelColumnName = 'Field',
  minWidth,
  columnWidth,
  labelColumnWidth,
  labelColumnClassName,
  tone = 'report',
  headerCellClassName,
  labelCellClassName,
  cellClassName,
  className,
  'data-testid': testId,
}: ComparisonTableFrameProps) {
  const t = TONE[tone]
  // Dynamic per-table geometry — the one place inline style is the right tool.
  const tableStyle: CSSProperties = {
    minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth,
  }
  const scrollStyle: CSSProperties = {
    overscrollBehaviorX: 'contain',
    touchAction: 'pan-x pan-y',
  }

  return (
    <div className={cn('relative min-w-0 max-w-full', className)}>
      <div
        className={cn(
          'max-w-full overflow-x-auto rounded-2xl border border-border pb-2',
          t.card
        )}
        style={scrollStyle}
        // Focusable so keyboard users can scroll the table sideways.
        tabIndex={0}
        data-testid={testId}
      >
        <table
          className='w-full table-fixed border-collapse text-sm'
          style={tableStyle}
        >
          {caption && <caption className='sr-only'>{caption}</caption>}
          <thead>
            <tr>
              <th
                scope='col'
                style={
                  labelColumnWidth ? { width: labelColumnWidth } : undefined
                }
                className={cn(
                  'sticky left-0 z-20 border-b p-4',
                  t.headerBorder,
                  t.headerTint,
                  labelColumnClassName
                )}
              >
                <span className='sr-only'>{labelColumnName}</span>
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope='col'
                  style={columnWidth ? { width: columnWidth } : undefined}
                  className={cn(
                    'border-b px-4 py-3 text-left text-xs font-medium text-foreground md:text-sm',
                    t.headerBorder,
                    col.highlighted ? t.highlightTint : t.headerTint,
                    headerCellClassName
                  )}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isLastRow = rowIndex === rows.length - 1
              return (
                <tr key={row.key}>
                  <th
                    scope='row'
                    className={cn(
                      'sticky left-0 z-10 p-4 text-left text-xs font-medium text-foreground md:text-sm',
                      t.stickyLabel,
                      !isLastRow && 'border-b border-border',
                      labelCellClassName
                    )}
                  >
                    {row.label}
                  </th>
                  {row.cells.map((cell, colIndex) => {
                    const col = columns[colIndex]
                    return (
                      <td
                        key={`${row.key}-${col?.key ?? colIndex}`}
                        className={cn(
                          'p-2.5 text-left text-xs font-normal text-foreground-tertiary md:text-sm',
                          !isLastRow && 'border-b border-border',
                          col?.highlighted && t.highlightTint,
                          cellClassName,
                          row.cellClassNames?.[colIndex]
                        )}
                      >
                        {cell}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-px right-px w-10 rounded-r-2xl bg-gradient-to-r from-transparent md:hidden',
          t.fade
        )}
      />
    </div>
  )
}
